<template>
  <div class="home-presence-section">
    <v-row no-gutters class="home-presence-row">
      <v-col cols="12" lg="6" class="home-map-column">
        <div
          class="home-map-panel"
          @mouseenter="mapHovered = true"
          @mouseleave="clearState"
          >
          <div class="home-map-stage">
            <svg
              class="home-map-svg"
              :viewBox="brazilMapViewBox"
              role="img"
              aria-label="Mapa de atuação da Brizola e Japur"
            >
              <desc>Mapa vetorial de estados baseado no projeto @svg-maps/brazil, licença CC BY 4.0.</desc>
              <path
                v-for="location in mapLocations"
                :key="location.id"
                :d="location.path"
                :class="[
                  'home-map-state',
                  {
                    'is-attended': location.marker,
                    'is-active': activeMarker && location.marker && activeMarker.uf === location.marker.uf
                  }
                ]"
                :tabindex="location.marker ? 0 : -1"
                :role="location.marker ? 'button' : null"
                :aria-label="location.marker ? `${location.marker.name}: ${courtLabel(location.marker.courts)}` : location.name"
                @mouseenter="selectLocation(location)"
                @focus="selectLocation(location)"
                @click="selectLocation(location)"
                @blur="activeMarker = null"
              />
            </svg>

            <div class="home-map-tooltip" :class="{ visible: activeMarker || mapHovered }">
              <strong>{{ activeMarker ? activeMarker.name : 'Atuação nacional' }}</strong>
              <span>{{ courtLabel(activeMarker ? activeMarker.courts : resolvedTotalCourts) }}</span>
              <span>{{ activeMarker ? activeMarker.cases : displayTotals.cases }} casos ativos</span>
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" lg="6" class="home-numbers-panel">
        <div class="home-numbers-content">
          <span class="home-numbers-eyebrow">Nossos números</span>

          <div class="home-metrics-grid">
            <div class="home-metric">
              <strong>{{ displayTotals.cases }}</strong>
              <span>casos ativos</span>
            </div>

            <div class="home-metric">
              <strong>{{ displayTotals.creditors }}</strong>
              <span>credores habilitados</span>
            </div>

            <div class="home-metric">
              <strong>{{ displayTotals.courts }}</strong>
              <span>comarcas atendidas</span>
            </div>
          </div>

          <div class="home-total-block">
            <strong>{{ displayTotals.totalValue }}</strong>
            <span>{{ numberLabels.totalValue }}</span>
          </div>

          <button type="button" class="home-cases-action" @click="openCases">
            consultar casos <span aria-hidden="true">→</span>
          </button>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios';
import brazilMap from '@svg-maps/brazil';
import statesList from '@/common/data/states-list.data';

const mapLocationsByUf = brazilMap.locations.reduce((locations, location) => {
  locations[location.id] = location;
  return locations;
}, {});

const stateMap = statesList.reduce((states, state) => {
  const uf = state.name.toUpperCase();
  const location = mapLocationsByUf[uf.toLowerCase()];

  states[state.id] = {
    uf,
    name: location ? location.name : uf
  };

  return states;
}, {});

export default {
  name: 'NationalPresenceSection',

  props: {
    cardNumbers: {
      type: Array,
      required: true
    },
    stateDistribution: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    brazilMapViewBox: brazilMap.viewBox,
    mapHovered: false,
    activeMarker: null,
    fallbackStateDistribution: []
  }),

  created() {
    if (!this.stateDistribution.length) this.loadPresenceFallback();
  },

  computed: {
    totalCourts() {
      return this.cardNumbers[3] && this.cardNumbers[3].number ? this.cardNumbers[3].number : 0;
    },

    mapMarkers() {
      const distribution = this.stateDistribution.length
        ? this.stateDistribution
        : this.fallbackStateDistribution;

      return distribution
        .map(item => {
          const state = stateMap[Number(item.state)];
          if (!state) return null;

          return {
            ...state,
            courts: Number(item.total_districts) || 0,
            cases: Number(item.total_cases) || 0
          };
        })
        .filter(Boolean);
    },

    mapLocations() {
      const markersByUf = this.mapMarkers.reduce((markers, marker) => {
        markers[marker.uf.toLowerCase()] = marker;
        return markers;
      }, {});

      return brazilMap.locations.map(location => ({
        ...location,
        marker: markersByUf[location.id] || null
      }));
    },

    resolvedTotalCourts() {
      if (!this.mapMarkers.length) return this.totalCourts;
      return this.mapMarkers.reduce((total, marker) => total + marker.courts, 0);
    },

    numberLabels() {
      return {
        cases: this.cardNumbers[0] ? this.cardNumbers[0].label : 'Número de casos ativos',
        creditors: this.cardNumbers[1] ? this.cardNumbers[1].label : 'Número de credores',
        totalValue: this.cardNumbers[2] ? this.cardNumbers[2].label : 'Total do passivo administrado',
        courts: this.cardNumbers[3] ? this.cardNumbers[3].label : 'Comarcas atendidas'
      };
    },

    displayTotals() {
      return {
        cases: this.cardNumbers[0] ? this.cardNumbers[0].number : 0,
        creditors: this.cardNumbers[1] ? this.cardNumbers[1].number : 0,
        courts: this.resolvedTotalCourts,
        totalValue: this.cardNumbers[2] ? this.cardNumbers[2].number : 'R$ 0,00'
      };
    }
  },

  methods: {
    openCases() {
      this.$router.push('/casos/recuperacoes');
    },

    courtLabel(total) {
      return Number(total) === 1 ? '1 comarca atendida' : `${total} comarcas atendidas`;
    },

    async loadPresenceFallback() {
      try {
        const result = await axios.get('/api/clients', { params: { page: 1, limit: 1000 } });
        const clients = result.data.content.data || [];
        const states = {};

        clients.forEach(client => {
          if (!client.state || !client.district_id) return;

          if (!states[client.state]) {
            states[client.state] = {
              state: client.state,
              total_cases: 0,
              districts: new Set()
            };
          }

          states[client.state].total_cases += 1;
          states[client.state].districts.add(client.district_id);
        });

        this.fallbackStateDistribution = Object.values(states).map(item => ({
          state: item.state,
          total_cases: item.total_cases,
          total_districts: item.districts.size
        }));
      } catch (error) {
        this.fallbackStateDistribution = [];
      }
    },

    selectMarker(marker) {
      this.activeMarker = marker;
      this.mapHovered = true;
    },

    selectLocation(location) {
      if (location.marker) this.selectMarker(location.marker);
    },

    clearState() {
      this.activeMarker = null;
      this.mapHovered = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.home-presence-section {
  margin-top: -7px;
}

.home-presence-row {
  background-color: var(--v-primary-base) !important;
}

.home-map-column {
  display: flex;
}

.home-numbers-panel {
  display: flex;
  min-height: 100%;
  padding: 0;
  background: var(--v-primary-base);
}

.home-map-panel {
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--v-darkSecondary-base);
}

.home-map-stage {
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  min-height: 560px;
  padding: 30px 42px;
}

.home-map-svg {
  display: block;
  width: min(72%, 620px);
  height: min(88%, 540px);
  overflow: visible;
  filter:
    drop-shadow(7px 9px 0 rgba(32, 0, 51, 0.24))
    drop-shadow(0 15px 18px rgba(32, 0, 51, 0.18));
  transform: translateY(-3px);
  transition: filter 0.24s ease, transform 0.24s ease;
}

.home-map-state {
  fill: #f5f0e6;
  stroke: rgba(32, 0, 51, 0.22);
  stroke-width: 1.2;
  vector-effect: non-scaling-stroke;
  transform-box: fill-box;
  transform-origin: center;
  transition: fill 0.22s ease, filter 0.22s ease, transform 0.22s ease;
  outline: none;
}

.home-map-state.is-attended {
  fill: var(--v-primary-base);
  stroke: rgba(204, 199, 211, 0.72);
  stroke-width: 0.72;
  cursor: pointer;
}

.home-map-panel:hover .home-map-svg {
  filter:
    drop-shadow(9px 11px 0 rgba(32, 0, 51, 0.27))
    drop-shadow(0 18px 22px rgba(32, 0, 51, 0.2));
  transform: translateY(-5px);
}

.home-map-state.is-attended:hover,
.home-map-state.is-attended:focus,
.home-map-state.is-attended.is-active {
  fill: #7258dc;
  filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
  transform: translateY(-2px) scale(1.018);
  animation: attended-state-pulse 1.25s ease-in-out infinite alternate;
}

@keyframes attended-state-pulse {
  from {
    filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.18));
  }

  to {
    filter: drop-shadow(0 0 13px rgba(255, 255, 255, 0.46));
  }
}

.home-map-tooltip {
  position: absolute;
  left: 42px;
  bottom: 38px;
  display: grid;
  gap: 3px;
  min-width: 210px;
  padding: 12px 16px;
  color: #fff;
  background: rgba(32, 0, 51, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  box-shadow: 0 10px 24px rgba(32, 0, 51, 0.18);
  opacity: 0;
  pointer-events: none;
  transform: translateY(8px);
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.home-map-tooltip.visible {
  opacity: 1;
  transform: translateY(0);
}

.home-map-tooltip strong {
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 600;
}

.home-map-tooltip span {
  color: rgba(255, 255, 255, 0.72);
  font-family: 'Metropolis' !important;
  font-size: 12px;
  font-weight: 400;
}

.home-numbers-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: clamp(42px, 5vw, 76px) clamp(44px, 5vw, 84px);
  color: #fff;
}

.home-numbers-eyebrow {
  font-family: 'Metropolis' !important;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}

.home-metrics-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.4fr) minmax(0, 0.8fr);
  margin-top: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
}

.home-metric {
  display: grid;
  align-content: start;
  gap: 12px;
  min-width: 0;
  padding: 0 30px;
  border-left: 1px solid rgba(255, 255, 255, 0.18);
}

.home-metric:first-child {
  padding-left: 0;
  border-left: 0;
}

.home-metric:last-child {
  padding-right: 0;
}

.home-metric strong,
.home-total-block strong {
  color: #fff;
  font-family: 'Metropolis' !important;
  font-weight: 300;
  letter-spacing: 0;
}

.home-metric strong {
  font-size: clamp(36px, 3.25vw, 56px);
  line-height: 1;
  white-space: nowrap;
}

.home-metric:nth-child(2) strong {
  font-size: clamp(36px, 3.05vw, 52px);
}

.home-metric span,
.home-total-block span {
  color: rgba(255, 255, 255, 0.66);
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.35;
}

.home-total-block {
  display: grid;
  gap: 10px;
  margin-top: 38px;
}

.home-total-block strong {
  font-size: clamp(34px, 3.8vw, 58px);
  line-height: 1.12;
  white-space: nowrap;
}

.home-cases-action {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 20px;
  margin-top: 42px;
  padding: 15px 24px;
  color: #fff;
  background: #7258dc;
  border: 0;
  border-radius: 4px;
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0;
  cursor: pointer;
  transition: background 0.16s ease, transform 0.16s ease;
}

.home-cases-action:hover {
  background: #8067e6;
  transform: translateY(-1px);
}

@media (max-width: 1264px) {
  .home-numbers-content {
    padding: 42px 38px;
  }

  .home-metric {
    padding: 0 22px;
  }
}

@media (max-width: 760px) {
  .home-map-stage {
    min-height: 460px;
    padding: 24px 18px;
  }

  .home-map-svg {
    width: min(92%, 460px);
    height: min(78%, 420px);
  }

  .home-numbers-content {
    padding: 38px 24px;
  }

  .home-metrics-grid {
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 30px;
    padding-bottom: 30px;
  }

  .home-metric,
  .home-metric:first-child,
  .home-metric:last-child {
    padding: 0 0 0 18px;
    border-left: 1px solid rgba(255, 255, 255, 0.18);
  }

  .home-map-tooltip {
    right: 18px;
    left: 18px;
    bottom: 18px;
  }

  .home-total-block strong {
    white-space: normal;
  }
}
</style>
